import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import PropTypes from "prop-types";
import { loadTask } from "~/store/actions/actions.tasks.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { getAllTimeRecords } from "~/store/selectors/selector.timeRecords.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
import { decimalToMinutes } from "~/utils/utils.jsx";
import TimeRecordListGroupedByDate from "./TimeRecordListGroupedByDate.jsx";
import { TimeRecordParentTotalContainer } from "./TimeRecordParentTotalContainer.jsx";
import { UserAvailableJobTypesRealtime } from "../JobTypes/UserAvailableJobTypesRealTime.js";
import { TimeRecordModalContainer } from "../Tracking/TimeRecordModalContainer.jsx";

const TimeRecordsListTaskModalContainer = ({ handleDismiss, taskId, projectId, trackedTime, open }) => {
  const dispatch = useDispatch();
  const task = useSelector((state) => getTaskById(state, taskId));
  const allTimeRecords = useSelector((state) => getAllTimeRecords(state));
  const loggedUser = useSelector(getLoggedUser);

  const taskTimeRecords = useMemo(() => {
    return allTimeRecords
      .filter(({ is_trashed, parent_type, parent_id }) => {
        return !is_trashed && parent_type === "Task" && parent_id === taskId;
      })
      .map((record) => ({
        id: record.id,
        jobTypeId: record.job_type_id,
        userId: record.user_id,
        value: record.value,
        recordDate: record.record_date,
        parentType: record.parent_type,
        parentId: record.parent_id,
        summary: record.summary,
        billableStatus: record.billable_status,
        createdOn: record.created_on,
        userName: record.user_name,
        userEmail: record.user_email,
      }));
  }, [allTimeRecords, taskId]);

  const total = useMemo(() => {
    return taskTimeRecords.reduce((acc, { value }) => {
      return acc + decimalToMinutes(value);
    }, 0);
  }, [taskTimeRecords]);

  const limitedAccessCount = useMemo(() => {
    const count = decimalToMinutes(trackedTime) - total;
    return count > 0 ? count : 0;
  }, [total, trackedTime]);

  const fetchTaskAfterEditTimeRecord = useCallback(() => {
    dispatch(loadTask(projectId, taskId, true));
  }, [dispatch, projectId, taskId]);

  return (
    <TimeRecordModalContainer
      open={open}
      onClose={handleDismiss}
      bodyClassName="time_record_form_modal_wrapper"
      parentId={taskId}
      parentType="Task"
      projectId={projectId}
    >
      <div className="time-record-form-modal">
        <div className="tw-flex tw-flex-row tw-gap-4 tw-justify-between tw-items-center">
          <div className="tw-truncate tw-mr-4 tw-text-base">
            <span className="tw-font-bold">{task.name}</span>
          </div>
          <div className="tw-shrink-0">
            <TimeRecordParentTotalContainer
              parentId={taskId}
              parentType="Task"
              showExpandSingle={false}
            />
          </div>
        </div>
        <div
          className={classNames("tw-mb-5 tw-mt-5", {
            "tw-border-solid tw-border-0 tw-border-b tw-border-border-primary": taskTimeRecords?.length,
          })}
        />
        <TimeRecordListGroupedByDate
          timeRecords={taskTimeRecords}
          limitedAccessCount={limitedAccessCount}
          onEdit={fetchTaskAfterEditTimeRecord}
          projectId={projectId}
          parentId={taskId}
        />
      </div>
      {loggedUser.get("class") !== "Client" && <UserAvailableJobTypesRealtime />}
    </TimeRecordModalContainer>
  );
};

TimeRecordsListTaskModalContainer.propTypes = {
  taskId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  handleDismiss: PropTypes.func,
  trackedTime: PropTypes.number,
  open: PropTypes.bool,
};

TimeRecordsListTaskModalContainer.defaultProps = {
  trackedTime: 0,
  open: false,
};

export default TimeRecordsListTaskModalContainer;
